import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

import { LayoutIcon } from '@phosphor-icons/react'

import { Alert, Button, ButtonWithSpinner, Text } from '@reveni-io/reveni-ui'

import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import UnsavedChangesModal from '@/common/components/UnsavedChangesModal/UnsavedChangesModal'
import { userPermissions } from '@/common/constants'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import routes from '@/Router/routes.constants'

import { orderTrackingPageTabs, previewModes } from '../constants'
import { getBlockDefinition, getBlockSettings, isBlockConfigurable } from '../helpers'
import useOrderTrackingPreview from '../hooks/useOrderTrackingPreview/useOrderTrackingPreview'
import AllPublishedChip from './AllPublishedChip/AllPublishedChip'
import BlockConfigurationSidebar from './BlockConfigurationSidebar/BlockConfigurationSidebar'
import BlocksSidebar from './BlocksSidebar/BlocksSidebar'
import HiddenBlocksBanner from './HiddenBlocksBanner/HiddenBlocksBanner'
import HistoryActions from './HistoryActions/HistoryActions'
import useBlockErrors from './hooks/useBlockErrors/useBlockErrors'
import useBlockImageUpload from './hooks/useBlockImageUpload/useBlockImageUpload'
import useBlockSelection from './hooks/useBlockSelection/useBlockSelection'
import useBlockSettings from './hooks/useBlockSettings/useBlockSettings'
import useBlocksSidebar from './hooks/useBlocksSidebar/useBlocksSidebar'
import useBlockVisibility from './hooks/useBlockVisibility/useBlockVisibility'
import useCustomScenario from './hooks/useCustomScenario/useCustomScenario'
import useHiddenBlocks from './hooks/useHiddenBlocks/useHiddenBlocks'
import usePreviewDate from './hooks/usePreviewDate/usePreviewDate'
import usePreviewSettings from './hooks/usePreviewSettings/usePreviewSettings'
import useRealOrder from './hooks/useRealOrder/useRealOrder'
import useRecommendedProductsService from './hooks/useRecommendedProductsService/useRecommendedProductsService'
import useSaveTrackingPageLayout from './hooks/useSaveTrackingPageLayout/useSaveTrackingPageLayout'
import useScenarioCriteria from './hooks/useScenarioCriteria/useScenarioCriteria'
import useScrollToBlock from './hooks/useScrollToBlock/useScrollToBlock'
import useTrackingPageLayout from './hooks/useTrackingPageLayout/useTrackingPageLayout'
import useUnsavedChangesModal from './hooks/useUnsavedChangesModal/useUnsavedChangesModal'
import useVisibilityCriteriaOptions from './hooks/useVisibilityCriteriaOptions/useVisibilityCriteriaOptions'
import LayoutErrorsChip from './LayoutErrorsChip/LayoutErrorsChip'
import PreviewSettingsSidebar from './PreviewSettingsSidebar/PreviewSettingsSidebar'
import PreviewToolbar from './PreviewToolbar/PreviewToolbar'
import TrackingPagePreview from './TrackingPagePreview/TrackingPagePreview'
import UnpublishedChangesChip from './UnpublishedChangesChip/UnpublishedChangesChip'
import { isBlockOffCanvas } from './helpers'
import * as Styles from './styles'

const visibilityCriteriaTabRoute = `${routes.configurationPage.orderTrackingPage.config}?tab=${orderTrackingPageTabs.visibilityCriteria}`

const TrackingPageEditorModal = ({ onClose, storeId, timeZone, smartTrackingEnabled }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { hasPermission } = useUserPermissions()
  const canEdit = hasPermission(userPermissions.editOrderTrackingPage)
  const { orderTrackingPreview, refreshOrderTrackingPreview } = useOrderTrackingPreview({ storeId })
  const {
    viewMode,
    viewModeOptions,
    isMobileView,
    layouts,
    layout,
    hasLayoutChanges,
    handleLayoutChange,
    handleLayoutChangeStart,
    handleLayoutChangeCommit,
    handleBlockHeightChange,
    addBlock,
    removeBlock,
    canUndo,
    handleUndo,
    canRedo,
    handleRedo,
  } = useTrackingPageLayout({ savedLayout: orderTrackingPreview?.layout })

  const { blockSettings, updateBlockSetting, nameBlock, resizeBlock, resetBlockSettings, hasBlockSettingsChanges } =
    useBlockSettings({
      savedLayout: orderTrackingPreview?.layout,
      layout,
    })

  const {
    blockVisibility,
    getBlockVisibility,
    updateBlockVisibility,
    resetBlockVisibility,
    hasBlockVisibilityChanges,
    saveBlockVisibility,
    refreshBlockVisibility,
  } = useBlockVisibility({ storeId, layout, blockSettings, timeZone })

  const { criteriaOptions, loadingCriteriaOptions } = useVisibilityCriteriaOptions({ storeId })

  const { blockStates, uploadBlockImage } = useBlockImageUpload({ storeId, updateBlockSetting, resizeBlock })

  const editorContext = { isEditor: true, onUploadImage: uploadBlockImage, blockStates }

  const { selectedBlockId, selectBlock, clearSelection } = useBlockSelection()
  const { showBlocksSidebar, openBlocksSidebar, closeBlocksSidebar } = useBlocksSidebar()
  const {
    previewMode,
    selectPreviewMode,
    showHiddenBlocks,
    changeShowHiddenBlocks,
    showPreviewSettings,
    openPreviewSettings,
    closePreviewSettings,
  } = usePreviewSettings({ storeId })
  const scenarioPreviewDate = usePreviewDate({ layout, getBlockVisibility, blockSettings, timeZone })
  const scenarioCriteria = useScenarioCriteria({ layout, getBlockVisibility, criteriaOptions, blockSettings })
  const { scenarioItems, scenarioShipments, scenarioReturnRequests, customScenarioPreview } = useCustomScenario({
    storeId,
    orderTrackingPreview,
    dateLimitedBlocks: scenarioPreviewDate.dateLimitedBlocks,
    criteriaLimitedBlocks: scenarioCriteria.criteriaLimitedBlocks,
  })
  const { selectedRealOrderId, selectRealOrder, realOrder, realOrderPreview } = useRealOrder({
    storeId,
    timeZone,
    orderTrackingPreview,
    layout,
    getBlockVisibility,
    criteriaOptions,
    isRealOrderTab: previewMode === previewModes.realOrder,
  })
  const { getRecommendedProductsService } = useRecommendedProductsService({
    storeId,
    isRealOrder: Boolean(realOrderPreview),
  })
  const { scrollContainerRef, scrollToBlock } = useScrollToBlock({ layout })

  const renderedPreviewMode = realOrderPreview ? previewModes.realOrder : previewModes.customScenario
  const { resolvedOrderTrackingPreview, hiddenBlocks } = realOrderPreview || customScenarioPreview

  const selectedDefinition = selectedBlockId && getBlockDefinition(selectedBlockId)
  const showBlockConfiguration = Boolean(selectedDefinition) && isBlockConfigurable(selectedDefinition)
  const openBlockId = showBlocksSidebar && showBlockConfiguration ? selectedBlockId : null

  const { hasErrors, committedLayoutErrors, committedErrorCount } = useBlockErrors({
    layout,
    blockSettings,
    getBlockVisibility,
    openBlockId,
  })

  const handlePublished = () => Promise.all([refreshOrderTrackingPreview(), refreshBlockVisibility()])

  const { handleSaveLayout, savingLayout } = useSaveTrackingPageLayout({
    storeId,
    layouts,
    blockSettings,
    saveBlockVisibility,
    onSaved: handlePublished,
  })

  const hasChanges = hasLayoutChanges || hasBlockSettingsChanges || hasBlockVisibilityChanges

  const { showUnsavedChangesModal, handleLeave, handleCancel, handleKeepEditing, handleDiscardChanges } =
    useUnsavedChangesModal({ hasChanges, onDiscard: onClose })

  const { isSelectedBlockHidden, hiddenBlockCount, showHiddenBlocksBanner } = useHiddenBlocks({
    hiddenBlocks,
    showHiddenBlocks,
    layout,
    selectedBlockId,
    isSelectedBlockOpen: Boolean(openBlockId),
    clearSelection,
  })

  const handleOpenPreviewSettings = () => {
    closeBlocksSidebar()
    openPreviewSettings()
  }

  const handleOpenBlocksSidebar = () => {
    closePreviewSettings()
    openBlocksSidebar()
  }

  const handleShowHiddenBlocks = () => changeShowHiddenBlocks(true)

  const handleAddBlock = blockId => {
    const newBlockId = addBlock(blockId)

    nameBlock(newBlockId)
    selectBlock(newBlockId)
    scrollToBlock(newBlockId)
  }

  const handleNavigateToBlock = blockId => {
    if (isBlockOffCanvas({ blockId, hiddenBlocks, showHiddenBlocks })) changeShowHiddenBlocks(true)

    handleOpenBlocksSidebar()
    selectBlock(blockId)
    scrollToBlock(blockId)
  }

  const handleRemoveBlock = blockId => {
    removeBlock(blockId)
    clearSelection()
  }

  const handleRemoveSelectedBlock = () => handleRemoveBlock(selectedBlockId)

  const handleResetSelectedBlock = () => {
    resetBlockSettings(selectedBlockId)
    resetBlockVisibility(selectedBlockId)
  }

  const handleChangeSelectedBlockVisibility = changes => updateBlockVisibility(selectedBlockId, changes)

  const handleGoToVisibilityCriteria = () => {
    handleLeave(() => {
      onClose()
      navigate(visibilityCriteriaTabRoute)
    })
  }

  const handleChangeSelectedBlockSetting = (key, value) => updateBlockSetting(selectedBlockId, key, value)

  const handleUploadSelectedBlockImage = file => uploadBlockImage(selectedBlockId, file)

  return createPortal(
    <Styles.Overlay>
      <Styles.Container>
        <Styles.Header>
          <Styles.Title>
            <LayoutIcon size={24} />
            <Text type="titleM">{t('Página de seguimiento')}</Text>
          </Styles.Title>
          <Styles.Actions data-testid="editor-actions">
            {layouts && (hasChanges ? <UnpublishedChangesChip /> : <AllPublishedChip />)}
            {committedErrorCount > 0 && (
              <LayoutErrorsChip
                layout={layout}
                layoutErrors={committedLayoutErrors}
                blockSettings={blockSettings}
                onNavigateToBlock={handleNavigateToBlock}
              />
            )}
            <HistoryActions canUndo={canUndo} onUndo={handleUndo} canRedo={canRedo} onRedo={handleRedo} />
            <Button variant="outlined" onClick={handleCancel}>
              {t('Cancelar')}
            </Button>
            <ButtonWithSpinner
              loading={savingLayout}
              disabled={!layouts || hasErrors || !canEdit}
              defaultLabel={t('Guardar y publicar')}
              loadingLabel={t('Guardando')}
              onClick={handleSaveLayout}
            />
          </Styles.Actions>
        </Styles.Header>
        <Styles.Body>
          <Styles.EditorArea>
            {!canEdit && (
              <Styles.PermissionAlert>
                <UnsavedChangesAlert hasPermission={false} />
              </Styles.PermissionAlert>
            )}
            <PreviewToolbar
              viewMode={viewMode}
              viewModeOptions={viewModeOptions}
              previewMode={renderedPreviewMode}
              onOpenPreviewSettings={handleOpenPreviewSettings}
              showBlocksSidebar={showBlocksSidebar}
              onOpenBlocksSidebar={handleOpenBlocksSidebar}
            />
            <Styles.PreviewDisclaimer>
              <Alert type="info">
                {t(
                  'La vista previa puede diferir de la página de seguimiento real, ya que algunas secciones dependen de los datos específicos del pedido.'
                )}
              </Alert>
            </Styles.PreviewDisclaimer>
            {showHiddenBlocksBanner && (
              <HiddenBlocksBanner hiddenBlockCount={hiddenBlockCount} onShowHiddenBlocks={handleShowHiddenBlocks} />
            )}
            <Styles.Content ref={scrollContainerRef}>
              <Styles.Page data-testid="tracking-page-preview-frame" isMobileView={isMobileView}>
                <TrackingPagePreview
                  orderTrackingPreview={resolvedOrderTrackingPreview}
                  viewMode={viewMode}
                  layout={layout}
                  blockSettings={blockSettings}
                  blockVisibility={blockVisibility}
                  blockErrors={committedLayoutErrors}
                  hiddenBlocks={hiddenBlocks}
                  showHiddenBlocks={showHiddenBlocks}
                  timeZone={timeZone}
                  editorContext={editorContext}
                  getRecommendedProductsService={getRecommendedProductsService}
                  onLayoutChange={handleLayoutChange}
                  onLayoutChangeStart={handleLayoutChangeStart}
                  onLayoutChangeCommit={handleLayoutChangeCommit}
                  onBlockHeightChange={handleBlockHeightChange}
                  onBlockResize={resizeBlock}
                  selectedBlockId={selectedBlockId}
                  onSelectBlock={selectBlock}
                  onRemoveBlock={handleRemoveBlock}
                />
              </Styles.Page>
            </Styles.Content>
          </Styles.EditorArea>
          {showPreviewSettings && (
            <PreviewSettingsSidebar
              previewMode={previewMode}
              onChangePreviewMode={selectPreviewMode}
              showHiddenBlocks={showHiddenBlocks}
              onChangeShowHiddenBlocks={changeShowHiddenBlocks}
              scenarioItems={scenarioItems}
              scenarioShipments={scenarioShipments}
              scenarioReturnRequests={scenarioReturnRequests}
              scenarioPreviewDate={scenarioPreviewDate}
              scenarioCriteria={scenarioCriteria}
              storeId={storeId}
              timeZone={timeZone}
              selectedRealOrderId={selectedRealOrderId}
              selectedRealOrderName={realOrder?.name}
              onSelectRealOrder={selectRealOrder}
              onClose={closePreviewSettings}
            />
          )}
          {showBlocksSidebar && layout && showBlockConfiguration && (
            <BlockConfigurationSidebar
              definition={selectedDefinition}
              settings={getBlockSettings(selectedDefinition, blockSettings[selectedBlockId])}
              isHidden={isSelectedBlockHidden}
              onOpenPreviewSettings={handleOpenPreviewSettings}
              onChangeSetting={handleChangeSelectedBlockSetting}
              onUploadImage={handleUploadSelectedBlockImage}
              canUploadImage={canEdit}
              visibility={getBlockVisibility(selectedBlockId)}
              timeZone={timeZone}
              onChangeVisibility={handleChangeSelectedBlockVisibility}
              criteriaOptions={criteriaOptions}
              loadingCriteriaOptions={loadingCriteriaOptions}
              onGoToCriteria={handleGoToVisibilityCriteria}
              onResetBlock={handleResetSelectedBlock}
              onRemoveBlock={handleRemoveSelectedBlock}
              onBack={clearSelection}
              onClose={closeBlocksSidebar}
            />
          )}
          {showBlocksSidebar && layout && !showBlockConfiguration && (
            <BlocksSidebar
              layout={layout}
              smartTrackingEnabled={smartTrackingEnabled}
              onAddBlock={handleAddBlock}
              onClose={closeBlocksSidebar}
            />
          )}
        </Styles.Body>
      </Styles.Container>
      <UnsavedChangesModal
        show={showUnsavedChangesModal}
        onKeepEditing={handleKeepEditing}
        onDiscardChanges={handleDiscardChanges}
      />
    </Styles.Overlay>,
    document.getElementById('modal')
  )
}

export default TrackingPageEditorModal
